import { useCounterpartiesQueryData } from '@src/providers/Counterparties/useCounterpartiesQueryData';
import {
  CounterpartyTypeEnum,
  IconName,
  compareDisplayNameOrName,
  type DisplayNameEntity,
  type FilterableSelectProperty,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { getCounterpartyFilterOptionLabel } from './useCounterpartyFilter';

type CounterpartyFilterOption = DisplayNameEntity & {
  Type?: CounterpartyTypeEnum | string;
};

interface UseAllCounterpartiesFilterParams<TKey extends string> {
  key?: TKey;
  label?: string;
}

interface UseCounterpartyFilterPropertyParams<TKey extends string> {
  key: TKey;
  label: string;
  getOptions: (counterparties: CounterpartyFilterOption[]) => string[];
}

function getCounterpartyFilterNames(
  counterparties: CounterpartyFilterOption[],
  includeCounterparty: (counterparty: CounterpartyFilterOption) => boolean = () => true
) {
  return counterparties
    .filter(counterparty => counterparty.Name.trim() !== '' && includeCounterparty(counterparty))
    .toSorted((a, b) => compareDisplayNameOrName(a, b))
    .map(counterparty => counterparty.Name);
}

function getAllCounterpartyFilterNames(counterparties: CounterpartyFilterOption[]) {
  return getCounterpartyFilterNames(counterparties);
}

function getCustomerCounterpartyFilterNames(counterparties: CounterpartyFilterOption[]) {
  return getCounterpartyFilterNames(
    counterparties,
    counterparty => counterparty.Type === CounterpartyTypeEnum.Customer
  );
}

function useCounterpartyFilterProperty<TKey extends string>({
  key,
  label,
  getOptions,
}: UseCounterpartyFilterPropertyParams<TKey>) {
  const { counterparties, counterpartiesByName } = useCounterpartiesQueryData();
  const counterpartyNames = useMemo(() => getOptions(counterparties), [counterparties, getOptions]);

  return useMemo(
    () =>
      ({
        key,
        label,
        field: 'Counterparty',
        icon: IconName.Counterparty,
        control: 'multi-select',
        options: counterpartyNames,
        getOptionLabel: name => getCounterpartyFilterOptionLabel(name, counterpartiesByName),
        clearUnsupportedSelectionsOnInit: false,
      }) as const satisfies FilterableSelectProperty<TKey, string>,
    [counterpartiesByName, counterpartyNames, key, label]
  );
}

export function useAllCounterpartiesFilter<TKey extends string = 'Counterparties'>({
  key = 'Counterparties' as TKey,
  label = 'Counterparties',
}: UseAllCounterpartiesFilterParams<TKey> = {}) {
  return useCounterpartyFilterProperty({
    key,
    label,
    getOptions: getAllCounterpartyFilterNames,
  });
}

export function useCustomerCounterpartiesFilter({ label = 'Customer' }: { label?: string } = {}) {
  return useCounterpartyFilterProperty({
    key: 'Customers',
    label,
    getOptions: getCustomerCounterpartyFilterNames,
  });
}
